import _ from 'lodash';
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { fetchUserThroughUsername } from '../../api/users';
import { getDiscoverUsers } from '../../api/discover';
import { DISCOVER_SORT_OPTIONS, DISCOVER_PAGE_SIZE, SEGMENT_OPTIONS } from '../../helpers/discover_helpers';
import { useVibeSearch } from './useVibeSearch';
import { useVibeExplainers } from './useVibeExplainers';
import './Discover.scss';

import DiscoverControls from './DiscoverControls';
import DiscoverResults from './DiscoverResults';
import DiscoverCategoryGrid from './DiscoverCategoryGrid';
import TagPill from '../General/TagPill';

import SearchInput from '../SearchInput/SearchInput';

const Discover = () => {
  const [usernameInput, setUsernameInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creators, setCreators] = useState([]);
  const [resolvedBrand, setResolvedBrand] = useState(null);

  const [page, setPage] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [noMoreResults, setNoMoreResults] = useState(false);

  const [sortBy, setSortBy] = useState('affinity');
  // Default ≥80% so Modal's bucketed pre-filter has a non-trivial floor on
  // the first call; "No minimum" stays available for users who want to widen.
  const [minAffinity, setMinAffinity] = useState(0.8);
  const [selectedSegments, setSelectedSegments] = useState(SEGMENT_OPTIONS.map(o => o.value));
  // Vibe-only: hide creators with unmatched query criteria (partial matches).
  // Default off — partial matches are shown.
  const [hidePartialMatches, setHidePartialMatches] = useState(false);
  const [affinityCounts, setAffinityCounts] = useState(null);
  const [viewMode, setViewMode] = useState('list');

  const [query, setQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Mirrors dropdown state for async chunk-arrival callbacks inside the vibe
  // hook (the user may have moved sort/affinity/segments since submission).
  const uiStateRef = useRef({ sortBy: 'affinity', minAffinity: 0.8, selectedSegments: SEGMENT_OPTIONS.map(o => o.value), hidePartialMatches: false });

  const [suggestedLists, setSuggestedLists] = useState([]);
  const [suggestedTags, setSuggestedTags] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);

  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadingMoreRef = useRef(false);
  const fetchIdRef = useRef(0);

  // Vibe search lifecycle (Modal fan-out, progressive merge, refill, retry,
  // local filter/sort) is encapsulated in useVibeSearch. The hook owns the
  // candidate set and chunk state; this component owns page-level display
  // and feeds back changes via the two callbacks below.
  const onVibeViewChange = useCallback(({ view }) => {
    setCreators(view.slice(0, DISCOVER_PAGE_SIZE));
    setPage(0);
    setNoMoreResults(view.length <= DISCOVER_PAGE_SIZE);
  }, []);
  const vibe = useVibeSearch({
    brand: resolvedBrand,
    fetchIdRef,
    uiStateRef,
    selectedTags,
    selectedLists,
    onViewChange: onVibeViewChange,
    onLoadingChange: setSearchLoading,
  });

  // activeVibeQuery namespaces the explainer cache and gates generation
  // (set on submit, cleared on reset).
  const [activeVibeQuery, setActiveVibeQuery] = useState(null);
  const {
    requestFor: requestExplainers,
    getExplainer,
    reset: resetExplainers,
  } = useVibeExplainers();

  const fmt = n => (typeof n === 'number' ? ` (${n.toLocaleString()})` : '');
  const isBrandTag = t => t.type === 'brandpromoter' || t.type === 'domainpromoter';
  // Vibe runs against an in-memory pool, not the affinity histogram — drop
  // the per-threshold counts when a vibe set is loaded.
  const minAffinityOptions = vibe.isActive
    ? [
        { value: null, label: 'No minimum' },
        { value: 0.25, label: '≥ 25%' },
        { value: 0.5, label: '≥ 50%' },
        { value: 0.7, label: '≥ 70%' },
        { value: 0.8, label: '≥ 80%' },
        { value: 0.9, label: '≥ 90%' },
        { value: 0.95, label: '≥ 95%' }
      ]
    : [
        { value: null, label: `No minimum${fmt(affinityCounts?.total)}` },
        { value: 0.25, label: `≥ 25%${fmt(affinityCounts?.thresholds?.[0.25])}` },
        { value: 0.5, label: `≥ 50%${fmt(affinityCounts?.thresholds?.[0.5])}` },
        { value: 0.7, label: `≥ 70%${fmt(affinityCounts?.thresholds?.[0.7])}` },
        { value: 0.8, label: `≥ 80%${fmt(affinityCounts?.thresholds?.[0.8])}` },
        { value: 0.9, label: `≥ 90%${fmt(affinityCounts?.thresholds?.[0.9])}` },
        { value: 0.95, label: `≥ 95%${fmt(affinityCounts?.thresholds?.[0.95])}` }
      ];

  const effectiveTotal = vibe.isActive
    ? vibe.getDisplayedView().length
    : !affinityCounts ? null
    : minAffinity != null ? (affinityCounts.thresholds[minAffinity] ?? null)
    : affinityCounts.total;

  // "Vibe Match" sort surfaces only while a vibe set is loaded — lets the
  // user return to Modal's ordering without re-running the query.
  const sortOptionsForUI = vibe.isActive
    ? [
        { label: 'Vibe', options: [{ value: 'vibe', label: 'Vibe Match', sublabel: 'Order from the #vibe semantic search.' }] },
        ...DISCOVER_SORT_OPTIONS
      ]
    : DISCOVER_SORT_OPTIONS;
  const selectedSortOption = _.flatten(_.map(sortOptionsForUI, 'options')).find(o => o.value === sortBy);


  const handleLoad = async () => {
    const username = usernameInput.trim();
    if (!username) return;

    setLoading(true);
    setError(null);
    setCreators([]);
    setResolvedBrand(null);
    setAffinityCounts(null);
    setPage(0);
    setNoMoreResults(false);
    resetExplainers();
    setActiveVibeQuery(null);

    try {
      const { user } = await fetchUserThroughUsername(username, { detailed: 1 });
      if (!user) {
        setError(`No user found for username "${username}"`);
        return;
      }
      if (!user.Brand_id) {
        setError(`User "${username}" does not have an associated Brand_id`);
        return;
      }

      const brand = { id: user.Brand_id, name: user.brand?.name || user.name };
      setResolvedBrand(brand);
      await fetchFirstPage(brand, { sortBy, minAffinity });
    } catch (e) {
      const msg = typeof e === 'string' ? e : 'Failed to load';
      setError(msg);
      window.ALERT.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = async () => {
    // In vibe mode, paginate from the hook's in-memory view rather than
    // re-fetching server-side. The view grows as chunks arrive and as the
    // user re-sorts / re-filters locally.
    const vibeView = vibe.isActive ? vibe.getDisplayedView() : null;
    if (vibeView) {
      const nextPage = page + 1;
      const start = nextPage * DISCOVER_PAGE_SIZE;
      const slice = vibeView.slice(start, start + DISCOVER_PAGE_SIZE);
      if (!slice.length) {
        setNoMoreResults(true);
        return;
      }
      setCreators(prev => [...prev, ...slice]);
      setPage(nextPage);
      setNoMoreResults(start + DISCOVER_PAGE_SIZE >= vibeView.length);
      return;
    }
    if (loadingMoreRef.current || !resolvedBrand || noMoreResults) return;
    loadingMoreRef.current = true;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const resp = await getDiscoverUsers(resolvedBrand.id, {
        sortBy, minAffinity, selectedSegments, query, page: nextPage, limit: DISCOVER_PAGE_SIZE, getDetailedResults: true,
        tagids: selectedTags.map(t => t.id),
        listids: selectedLists.map(l => l.id)
      });
      const list = Array.isArray(resp?.results) ? resp.results : [];

      if (list.length === 0) {
        setNoMoreResults(true);
      } else {
        setCreators(prev => [...prev, ...list]);
        setPage(nextPage);
        // See note at fetchFirstPage about partial-page detection.
        if (list.length < DISCOVER_PAGE_SIZE) setNoMoreResults(true);
      }
    } catch (e) {
      const msg = 'Failed to load more creators — change a filter to retry.';
      setError(msg);
      setNoMoreResults(true);
      window.ALERT.error(msg);
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  // Write loadMore into a ref each render so the stable useCallback below always
  // calls the latest closure without needing it as a dependency.
  loadMoreRef.current = loadMore;
  // Latest dropdown state for async chunk callbacks.
  uiStateRef.current = { sortBy, minAffinity, selectedSegments, hidePartialMatches };

  // Orient the admin when the partial-match toggle filters the whole view empty
  // (applies to both list and category views).
  const partialMatchesEmptyMessage = vibe.isActive && hidePartialMatches
    ? 'No full matches to show — turn off “Hide partial matches” to include partial matches.'
    : undefined;

  const lastCardRef = useCallback(node => {
    if (observerRef.current) observerRef.current.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) loadMoreRef.current();
    });
    observerRef.current.observe(node);
  }, []);

  useEffect(() => () => observerRef.current?.disconnect(), []);

  // Keyed on `creators` so it covers both the first page and infinite-scroll
  // appends; requestExplainers only queues newcomers, so re-runs are cheap.
  useEffect(() => {
    if (!vibe.isActive || !resolvedBrand || !activeVibeQuery) return;
    requestExplainers(creators, {
      brandId: resolvedBrand.id,
      vibeQuery: activeVibeQuery,
      disambiguation: vibe.disambiguation,
    });
  }, [creators, vibe.isActive, vibe.disambiguation, resolvedBrand, activeVibeQuery, requestExplainers]);

  const fetchFirstPage = async (brand, opts = {}) => {
    const fetchId = ++fetchIdRef.current;
    // A /Discover/users fetch supersedes any prior vibe set.
    vibe.reset();

    const effectiveSortBy = opts.sortBy !== undefined ? opts.sortBy : sortBy;
    const effectiveMinAffinity = opts.minAffinity !== undefined ? opts.minAffinity : minAffinity;
    const effectiveSegments = opts.selectedSegments !== undefined ? opts.selectedSegments : selectedSegments;
    const effectiveQuery = opts.query !== undefined ? opts.query : query;
    const skipCounts = opts.skipCounts === true;
    const effectiveTagIds  = opts.tagids  ?? selectedTags.map(t => t.id);
    const effectiveListIds = opts.listids ?? selectedLists.map(l => l.id);

    try {
      const resp = await getDiscoverUsers(brand.id, {
        sortBy: effectiveSortBy,
        minAffinity: effectiveMinAffinity,
        selectedSegments: effectiveSegments,
        page: 0,
        limit: DISCOVER_PAGE_SIZE,
        getDetailedResults: true,
        skipCounts,
        query: effectiveQuery,
        tagids: effectiveTagIds,
        listids: effectiveListIds
      });
      if (fetchId !== fetchIdRef.current) return;
      const list = Array.isArray(resp?.results) ? resp.results : [];
      setCreators(list);
      setPage(0);
      // Relies on the backend returning a partial page when fewer than
      // DISCOVER_PAGE_SIZE rows remain (true today given LIMIT-driven SQL in
      // node/utils/db/discover.js). If that route ever gains JS-side
      // post-filtering or row-dropping, this silently breaks "load more" and
      // should revert to `list.length === 0`.
      setNoMoreResults(list.length < DISCOVER_PAGE_SIZE);
      if (!skipCounts) setAffinityCounts(resp?.affinityCounts ?? null);
      setSuggestedLists(Array.isArray(resp?.lists) ? resp.lists : []);
      setSuggestedTags(Array.isArray(resp?.tags) ? resp.tags : []);
    } catch (e) {
      if (fetchId !== fetchIdRef.current) return;
      const msg = typeof e === 'string' ? e : 'Failed to load';
      setError(msg);
      window.ALERT.error(msg);
    }
  };

  const handleSortChange = async option => {
    const newSortBy = option.value;
    setSortBy(newSortBy);
    setNoMoreResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (vibe.isActive) {
      vibe.recompute({ sortBy: newSortBy, minAffinity, segments: selectedSegments, hidePartialMatches });
      return;
    }
    setCreators([]);
    if (resolvedBrand) await fetchFirstPage(resolvedBrand, { sortBy: newSortBy, minAffinity, skipCounts: true });
  };

  const handleMinAffinityChange = async option => {
    const newMinAffinity = option.value;
    setMinAffinity(newMinAffinity);
    setNoMoreResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (vibe.isActive) {
      // Raised/equal → local filter only (refillTo is a no-op). Lowered →
      // hook refills the missing range, then we re-filter.
      const newFloor = newMinAffinity != null ? newMinAffinity : 0;
      await vibe.refillTo(newFloor);
      vibe.recompute({ sortBy, minAffinity: newMinAffinity, segments: selectedSegments, hidePartialMatches });
      return;
    }
    setCreators([]);
    if (resolvedBrand) await fetchFirstPage(resolvedBrand, { sortBy, minAffinity: newMinAffinity, skipCounts: true });
  };

  const handleSegmentToggle = async segmentValue => {
    const next = selectedSegments.includes(segmentValue)
      ? selectedSegments.filter(s => s !== segmentValue)
      : [...selectedSegments, segmentValue];
    setSelectedSegments(next);
    setNoMoreResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (vibe.isActive) {
      vibe.recompute({ sortBy, minAffinity, segments: next, hidePartialMatches });
      return;
    }
    setCreators([]);
    if (resolvedBrand) {
      await fetchFirstPage(resolvedBrand, { sortBy, minAffinity, selectedSegments: next });
    }
  };

  // Vibe-only local filter — no server fetch (evidence exists only on vibe hits).
  const handleHidePartialMatchesToggle = () => {
    const next = !hidePartialMatches;
    setHidePartialMatches(next);
    setNoMoreResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (vibe.isActive) vibe.recompute({ sortBy, minAffinity, segments: selectedSegments, hidePartialMatches: next });
  };

  const VIBE_PREFIX = /^#vibe\s+/i;

  const handleQuerySubmit = async e => {
    e?.preventDefault();
    if (!resolvedBrand) return;
    const trimmed = query.trim();
    if (VIBE_PREFIX.test(trimmed)) {
      const vibeQuery = trimmed.replace(VIBE_PREFIX, '').trim();
      if (!vibeQuery) return;
      setSortBy('vibe');
      setCreators([]);
      setNoMoreResults(false);
      resetExplainers();
      setActiveVibeQuery(vibeQuery);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      const floor = minAffinity != null ? minAffinity : 0;
      await vibe.submit(vibeQuery, floor);
      return;
    }
    setQuery(trimmed);
    setCreators([]);
    setNoMoreResults(false);
    vibe.reset();
    resetExplainers();
    setActiveVibeQuery(null);
    if (sortBy === 'vibe') setSortBy('affinity');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchLoading(true);
    try {
      await fetchFirstPage(resolvedBrand, { query: trimmed, skipCounts: true });
    } finally {
      setSearchLoading(false);
    }
  };

  // Shared pipeline for any change to the active filter set. `clearQuery` is
  // true when the change originates from picking a suggestion (the typed
  // query has done its job and would otherwise keep narrowing results by name).
  const applyFilterChange = async ({ nextLists, nextTags, clearQuery = false }) => {
    setSelectedLists(nextLists);
    setSelectedTags(nextTags);
    if (clearQuery) {
      setSuggestedLists([]);
      setSuggestedTags([]);
      setQuery('');
      vibe.reset();
    }
    setCreators([]);
    setNoMoreResults(false);
    setSearchLoading(true);
    try {
      await fetchFirstPage(resolvedBrand, {
        listids: nextLists.map(l => l.id),
        tagids: nextTags.map(t => t.id),
        ...(clearQuery ? { query: '' } : {})
      });
    } finally {
      setSearchLoading(false);
    }
  };

  const handleSelectList = list => {
    if (selectedLists.some(l => l.id === list.id)) return;
    return applyFilterChange({
      nextLists: [...selectedLists, { id: list.id, title: list.title, userCount: list.userCount }],
      nextTags: selectedTags,
      clearQuery: true
    });
  };

  const handleRemoveList = id => applyFilterChange({
    nextLists: selectedLists.filter(l => l.id !== id),
    nextTags: selectedTags
  });

  const handleSelectTag = tag => {
    if (selectedTags.some(t => t.id === tag.id)) return;
    return applyFilterChange({
      nextLists: selectedLists,
      nextTags: [...selectedTags, { id: tag.id, value: tag.value, type: tag.type, userCount: tag.userCount }],
      clearQuery: true
    });
  };

  const handleRemoveTag = id => applyFilterChange({
    nextLists: selectedLists,
    nextTags: selectedTags.filter(t => t.id !== id)
  });

  const handleQueryClear = async () => {
    setQuery('');
    vibe.reset();
    if (sortBy === 'vibe') setSortBy('affinity');
    if (!resolvedBrand) return;
    setCreators([]);
    setNoMoreResults(false);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setSearchLoading(true);
    setSuggestedLists([]);
    setSuggestedTags([]);
    try {
      await fetchFirstPage(resolvedBrand, { query: '', skipCounts: true });
    } finally {
      setSearchLoading(false);
    }
  };

  const activeListPills = selectedLists.map(list => ({
    key: `list-${list.id}`,
    label: `List: ${list.title}`,
    onRemove: () => handleRemoveList(list.id)
  }));
  const activeTagPills = selectedTags.map(tag => ({
    key: `tag-${tag.id}`,
    label: `${isBrandTag(tag) ? 'Brand' : 'Tag'}: ${tag.value}`,
    onRemove: () => handleRemoveTag(tag.id)
  }));

  return (
    <div className='discover-outer'>
      <div className='discover-header'>
        <h1 className='discover-title'>Discover Creators for Your Brand</h1>
        <div className='discover-controls'>
          <input
            type='text'
            placeholder='Brand username (e.g. shopmy)'
            value={usernameInput}
            onChange={e => setUsernameInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLoad()}
          />
          <button onClick={handleLoad} disabled={loading || !usernameInput.trim()}>
            {loading ? 'Loading…' : 'Load'}
          </button>
        </div>
        {resolvedBrand && (
          <div className='discover-resolved-brand'>
            <div>Showing results for <strong>{resolvedBrand.name}</strong> (Partner Brand ID: {resolvedBrand.id})</div>
            <div className='discover-resolved-brand-description'>Brand Match Score measures a creator's taste against your brand. Using creators' pinned products over the past 365 days. Updated daily.</div>
          </div>
        )}
        {resolvedBrand && (
          <form className='discover-query-form' onSubmit={handleQuerySubmit}>
            <SearchInput
              value={query}
              onChange={e => setQuery(e.target.value)}
              onClear={handleQueryClear}
              placeholder='Search creators, brands, tags, or lists (or #vibe to add details)…'
              showSearchIcon
              size='lg'
              isLoading={searchLoading}
              className='discover-query-input'
            />
          </form>
        )}
        {(suggestedLists.length > 0 || suggestedTags.length > 0) && (
          <div className='discover-suggestions'>
            {suggestedLists.map(list => (
              <button
                type='button'
                key={`list-${list.id}`}
                className='discover-suggestion'
                onClick={() => handleSelectList(list)}
              >
                List: {list.title}
                {typeof list.userCount === 'number' && (
                  <span className='discover-suggestion-count'> ({list.userCount.toLocaleString()})</span>
                )}
              </button>
            ))}
            {suggestedTags.map(tag => (
              <button
                type='button'
                key={`tag-${tag.id}`}
                className='discover-suggestion'
                onClick={() => handleSelectTag(tag)}
              >
                {isBrandTag(tag) ? 'Brand' : 'Tag'}: {tag.value}
                {typeof tag.userCount === 'number' && (
                  <span className='discover-suggestion-count'> ({tag.userCount.toLocaleString()})</span>
                )}
              </button>
            ))}
          </div>
        )}
        {resolvedBrand && vibe.error === 'no_matches' && (
          <div className='discover-vibe-empty'>
            End of vibe matches — loosen filters or refine the query.
          </div>
        )}
        {resolvedBrand && vibe.error === 'service_unavailable' && (
          <div className='discover-vibe-error'>
            Vibe search is temporarily unavailable. Try again in a moment, or search by name / tag / list.
          </div>
        )}
        {resolvedBrand && vibe.loadingChunks > 0 && (
          <div className='discover-vibe-loading-more'>
            Loading more matches down to ≥{Math.round(vibe.loadedFloor * 100)}%
            {vibe.loadedCount > 0 && ` — ${vibe.loadedCount.toLocaleString()} loaded so far`}…
          </div>
        )}
        {resolvedBrand && vibe.loadingChunks === 0 && vibe.hasFailedChunks && (
          <div className='discover-vibe-partial-failure'>
            Some matches couldn't load.{' '}
            <button type='button' className='discover-vibe-retry' onClick={vibe.retry}>
              Retry
            </button>
          </div>
        )}
        {error && <div className='discover-error'>{error}</div>}
      </div>
      {resolvedBrand && (
        <DiscoverControls
          selectedSortOption={selectedSortOption}
          groupedSortOptions={sortOptionsForUI}
          onSortChange={handleSortChange}
          minAffinity={minAffinity}
          minAffinityOptions={minAffinityOptions}
          onMinAffinityChange={handleMinAffinityChange}
          selectedSegments={selectedSegments}
          onSegmentToggle={handleSegmentToggle}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          vibeActive={vibe.isActive}
          hidePartialMatches={hidePartialMatches}
          onHidePartialMatchesToggle={handleHidePartialMatchesToggle}
        />
      )}
      {(activeListPills.length > 0 || activeTagPills.length > 0) && (
        <div
          className='discover-active-filters'
          title='Each pill shows (creators matching all active filters / creators matching this filter alone)'
        >
          {[...activeListPills, ...activeTagPills].map(pill => (
            <TagPill key={pill.key} tag={pill.label} onRemove={pill.onRemove} />
          ))}
        </div>
      )}
      {viewMode === 'list' ? (
        <DiscoverResults
          creators={creators}
          sortBy={sortBy}
          effectiveTotal={effectiveTotal}
          loadingMore={loadingMore}
          noMoreResults={noMoreResults}
          lastCardRef={lastCardRef}
          disambiguation={vibe.disambiguation}
          emptyMessage={partialMatchesEmptyMessage}
          getExplainer={vibe.isActive && !vibe.disambiguation?.degraded ? getExplainer : undefined}
        />
      ) : (
        <DiscoverCategoryGrid
          creators={creators}
          sortBy={sortBy}
          loadMore={loadMore}
          loadingMore={loadingMore}
          noMoreResults={noMoreResults}
          disambiguation={vibe.disambiguation}
          emptyMessage={partialMatchesEmptyMessage}
        />
      )}
    </div>
  );
};

export default Discover;
